import { Package, CurrencyDollar, TrendUp01, TrendDown01 } from '@untitledui/icons';
import { useTranslation } from 'react-i18next';
import { calculateTargetPercentage } from '@/utils/week-utils';

interface TrendCardProps {
    title: string;
    value: number;
    target: number;
    icon: React.ComponentType<{ className?: string }>;
    color: 'brand' | 'success' | 'warning' | 'error';
    formatValue?: (value: number) => string;
}

const TrendCard = ({ title, value, target, icon: Icon, color, formatValue }: TrendCardProps) => {
    const colorClasses = {
        brand: 'text-brand-600 bg-brand-50',
        success: 'text-success-600 bg-success-50',
        warning: 'text-warning-600 bg-warning-50',
        error: 'text-error-600 bg-error-50'
    };

    const percentage = calculateTargetPercentage(value, target);
    const isPositive = percentage >= 100;
    const TrendIcon = isPositive ? TrendUp01 : TrendDown01;

    const formattedValue = formatValue ? formatValue(value) : value.toLocaleString();
    const formattedTarget = formatValue ? formatValue(target) : target.toLocaleString();

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <div className="flex items-center justify-between mb-4">
                <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
                    <Icon className="size-6" />
                </div>
                <div className={`flex items-center gap-1 ${isPositive ? 'text-success-600' : 'text-error-600'}`}>
                    <TrendIcon className="size-4" />
                    <span className="text-sm font-medium">{percentage}%</span>
                </div>
            </div>
            <div>
                <p className="text-sm font-medium text-tertiary mb-1">{title}</p>
                <p className="text-2xl font-semibold text-primary">{formattedValue}</p>
                <p className="text-xs text-tertiary mt-1">
                    Target: {formattedTarget}
                </p>
            </div>
        </div>
    );
};

interface TrendCardsProps {
    yearlyContainers: number;
    yearlyRevenue: number;
    yearlyContainerTarget: number;
    yearlyRevenueTarget: number;
    monthlyContainers: number;
    monthlyRevenue: number;
    monthlyContainerTarget: number;
    monthlyRevenueTarget: number;
}

export const TrendCards = ({
    yearlyContainers,
    yearlyRevenue,
    yearlyContainerTarget,
    yearlyRevenueTarget,
    monthlyContainers,
    monthlyRevenue,
    monthlyContainerTarget,
    monthlyRevenueTarget,
}: TrendCardsProps) => {
    const { t } = useTranslation(['pages:calendar', 'common']);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    return (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <TrendCard
                title={t('trendCards.yearlyContainers')}
                value={yearlyContainers}
                target={yearlyContainerTarget}
                icon={Package}
                color="brand"
            />
            <TrendCard
                title={t('trendCards.yearlyRevenue')}
                value={yearlyRevenue}
                target={yearlyRevenueTarget}
                icon={CurrencyDollar}
                color="success"
                formatValue={formatCurrency}
            />
            <TrendCard
                title={t('trendCards.monthlyContainers')}
                value={monthlyContainers}
                target={monthlyContainerTarget}
                icon={Package}
                color="brand"
            />
            <TrendCard
                title={t('trendCards.monthlyRevenue')}
                value={monthlyRevenue}
                target={monthlyRevenueTarget}
                icon={CurrencyDollar}
                color="success"
                formatValue={formatCurrency}
            />
        </div>
    );
};

